/**
 * ThemeToggle — matches the cycle designer's ghost button aesthetic.
 *
 * In light mode: Sun icon + "DARK"  (offers to switch to dark)
 * In dark mode:  Moon icon + "LIGHT" (offers to switch to light)
 *
 * Styles are CSS-variable driven so the button always looks correct
 * in whichever theme is currently active.
 */

import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { monoIconButton } from '@/components/mono/iconButton';

interface ThemeToggleProps {
  /** Extra classes on the outer button — use for layout only (e.g. 'w-full') */
  className?: string;
  /**
   * 'compact' = icon only (legacy inline styles)
   * 'labeled' = icon + text, auto width (default)
   * 'full'    = icon + text, 100% width
   * 'icon'    = icon only, Tailwind — matches prioritization top bar ghost buttons
   */
  variant?: 'compact' | 'labeled' | 'full' | 'icon';
}

export function ThemeToggle({ className = '', variant = 'labeled' }: ThemeToggleProps) {
  const { isDark, toggleTheme } = useTheme();

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        className={monoIconButton('sm', className)}
      >
        {isDark ? (
          <Sun className="h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <Moon className="h-4 w-4 shrink-0" aria-hidden />
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        height: isDark ? '28px' : '32px',
        padding: variant === 'compact' ? '0 8px' : '0 12px',
        background: isDark ? 'none' : '#f3f4f6',
        border: '1px solid',
        borderColor: isDark ? 'var(--rule-bright)' : '#d1d5db',
        borderRadius: isDark ? '2px' : '6px',
        cursor: 'pointer',
        fontFamily: "'degular-variable', -apple-system, BlinkMacSystemFont, 'fieldwork', system-ui, sans-serif",
        fontSize: '0.75rem',
        fontWeight: 500,
        letterSpacing: '0',
        textTransform: 'none',
        color: isDark ? 'var(--text-ter)' : '#374151',
        whiteSpace: 'nowrap',
        transition: 'background 0.15s, color 0.15s, border-color 0.15s',
        width: variant === 'full' ? '100%' : undefined,
        justifyContent: variant === 'full' ? 'flex-start' : undefined,
      }}
      onMouseEnter={e => {
        const btn = e.currentTarget;
        btn.style.background = isDark ? 'rgba(255,255,255,0.04)' : '#e5e7eb';
        btn.style.color = isDark ? 'var(--text-sec)' : '#111827';
        btn.style.borderColor = isDark ? 'var(--slate)' : '#9ca3af';
      }}
      onMouseLeave={e => {
        const btn = e.currentTarget;
        btn.style.background = isDark ? 'none' : '#f3f4f6';
        btn.style.color = isDark ? 'var(--text-ter)' : '#374151';
        btn.style.borderColor = isDark ? 'var(--rule-bright)' : '#d1d5db';
      }}
    >
      {isDark
        ? <Sun style={{ width: '11px', height: '11px', flexShrink: 0 }} />
        : <Moon style={{ width: '14px', height: '14px', flexShrink: 0 }} />
      }
      {variant !== 'compact' && (
        <span>{isDark ? 'Light mode' : 'Switch to dark'}</span>
      )}
    </button>
  );
}
